import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import AccessDeviceIcons from './AccessDeviceIcons';
import ExperienceRating from './ExperienceRating';

const CATEGORY_COLORS = {
  'Featured': '#ff00aa',
  'RealityCheck Original': '#e040c8',
  '360°': '#00f5ff',
  'AR': '#45B7D1',
  'AR Portal': '#00aaff',
  'AR Performance': '#00ff88',
  'AR Art': '#ff66ff',
  'AR Character': '#ff0066',
  'AR Game': '#ffee00',
  'AR Location': '#00ff66',
  'LBE': '#e85d04',
  'Location-Based': '#e85d04',
  'Metaverse': '#9d4edd',
  'Virtual Reality': '#2196F3'
};

const Tile = styled(motion.div)`
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  background: rgba(30, 20, 50, 0.6);
  aspect-ratio: 16 / 10;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
  }

  /* Bento: first tile spans 2 cols for hero effect */
  &:first-child {
    grid-column: span 2;
    grid-row: span 2;
    aspect-ratio: auto;
    min-height: 240px;
  }
`;

const Thumbnail = styled.div`
  position: absolute;
  inset: 0;
  background: url(${props => props.$src}) center/cover;
  transition: transform 0.4s ease;

  ${Tile}:hover & {
    transform: scale(1.05);
  }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.9) 0%,
    rgba(0, 0, 0, 0.4) 40%,
    transparent 70%
  );
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: 16px;
`;

const CategoryBadge = styled.span`
  position: absolute;
  top: 10px;
  left: 10px;
  background: ${props => CATEGORY_COLORS[props.$category] || '#00f5ff'};
  color: ${props => (props.$category === 'AR Game' ? '#000' : '#fff')};
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 0.7rem;
  font-weight: 700;
`;

const StatusBadge = styled.span`
  position: absolute;
  top: 10px;
  right: 10px;
  background: ${(p) => (p.$status === 'approved' ? '#00c853' : '#ff9800')};
  color: #fff;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 0.7rem;
  font-weight: 700;
`;

const SaveButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  color: ${props => (props.$filled ? '#ff00aa' : '#fff')};
  font-size: 1.1rem;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }
`;

const Title = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.8);
  line-height: 1.2;
`;

const Meta = styled.span`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.8);
  margin-top: 4px;
`;

const TileDeviceIcons = styled(AccessDeviceIcons)`
  margin-top: 6px;
  pointer-events: none;
`;

function ExperienceBentoTile({
  experience,
  index,
  variants,
  onExperienceClick,
  showSaveButton,
  isSaved,
  onSaveClick
}) {
  const handleClick = (e) => {
    if (e.target.closest('button')) return;
    onExperienceClick?.(experience);
  };

  const handleSaveClick = (e) => {
    e.stopPropagation();
    onSaveClick?.(experience.id, e.currentTarget);
  };

  return (
    <Tile
      variants={variants}
      initial="hidden"
      animate="visible"
      custom={index}
      onClick={handleClick}
    >
      <Thumbnail $src={experience.thumbnail} />
      <Overlay>
        {showSaveButton && (
          <SaveButton
            type="button"
            $filled={isSaved}
            onClick={handleSaveClick}
            aria-label={isSaved ? 'Unsave' : 'Save'}
          >
            {isSaved ? '★' : '☆'}
          </SaveButton>
        )}
        <CategoryBadge $category={experience.categoryChip}>
          {experience.categoryChip}
        </CategoryBadge>
        {experience.status === 'pending' && (
          <StatusBadge $status="pending">Pending</StatusBadge>
        )}
        <Title>{experience.title}</Title>
        <ExperienceRating experienceId={experience.id} compact />
        <Meta>{experience.duration}</Meta>
        <TileDeviceIcons experience={experience} compact />
      </Overlay>
    </Tile>
  );
}

export default ExperienceBentoTile;
